import { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useQueryClient, useQuery } from "@tanstack/react-query";
import api, { formatApiErrorDetail } from "@/lib/api";
import Layout from "@/components/Layout";
import RoofMap from "@/components/RoofMap";
import EstimateBreakdown from "@/components/EstimateBreakdown";
import ChoixMateriaux from "@/components/ChoixMateriaux";
import GuideTrace, { guideTraceAVoir } from "@/components/GuideTrace";
import TotalMesure from "@/components/TotalMesure";
import { PITCH_OPTIONS, PITCH_VIDE, pitchDefinie, pitchFromEstimate, computeEstimateBatiments, footprintFromPolygon, estToitPlat, money, paireLangues, prixConfigures, distanceM } from "@/lib/roof";
import { useI18n } from "@/i18n/I18nContext";
import { dateCourte } from "@/lib/dates";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Search, Loader2, Save, MapPin, Sparkles, AlertTriangle, Move, RotateCcw, Plus, X, Home, Box, ArrowRight, Clock, Ruler, SatelliteDish, Slash, HelpCircle, ZoomIn, ZoomOut, ChevronUp, ChevronDown, ChevronLeft, ChevronRight, Crosshair } from "lucide-react";
import { toast } from "sonner";

const uid = () => Math.random().toString(36).slice(2, 9);
const DEFAULT_RECT = [[0.4, 0.4], [0.6, 0.4], [0.6, 0.6], [0.4, 0.6]];
// Langues offertes pour le PDF client : la paire du PAYS du compte (ca : fr/en,
// us : en/es) — un couvreur américain sert des clients hispanophones, pas
// francophones. Même règle que Réglages → Entreprise.
const NOMS_LANGUES = { fr: "Français", en: "English", es: "Español" };

// Freins de l'autocomplétion Google. Réglés par Jeff : seuil de caractères le
// 2026-08-03, délai ramené à 0,8 s le 2026-08-06 — 1,5 s se sentait comme une
// latence. Ce n'est pas une question d'argent (Google offre 10 000 appels par
// mois et par service, hors d'atteinte au volume actuel) mais de bruit à
// l'écran : la suggestion arrive une fois l'adresse engagée, pas à chaque
// frappe. ⚠️ Le serveur applique le même seuil de caractères — si tu changes
// AUTOCOMPLETE_MIN, change aussi `places_autocomplete` dans server.py.
const AUTOCOMPLETE_MIN = 10;     // caractères tapés avant le premier appel
const AUTOCOMPLETE_DELAI = 800;  // ms d'inactivité au clavier avant l'appel
// En deçà de cette distance, le centre d'un contour désigne le même bâtiment :
// inutile de repayer un appel Solar pour obtenir la même réponse.
const SOLAR_MEME_POINT_M = 6;
// Au-delà, le bâtiment rendu par Solar n'est probablement pas celui qu'on
// visait : on ne l'accepte plus automatiquement.
const SOLAR_ECART_DOUTEUX_M = 20;

export default function NewEstimate({ mode = "soumission" }) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { t, lang } = useI18n();
  const fr = lang !== "en";
  // Mode « mesure » : le même atelier (adresse, satellite, contours, sections),
  // mais sans le chiffrage — on enregistre un relevé, pas une soumission.
  const modeMesure = mode === "mesure";
  const [address, setAddress] = useState("");
  const [analyzing, setAnalyzing] = useState(false);
  const [result, setResult] = useState(null);
  const [settings, setSettings] = useState(null);
  // Guide des trois gestes du tracé : à la toute première image, puis jamais —
  // sauf si le couvreur le rouvre lui-même.
  const [guideTrace, setGuideTrace] = useState(false);
  // « satellite » quand Sat Premium a mesuré (rien à tracer), « trace » sinon.
  const [guideVariante, setGuideVariante] = useState("trace");
  // Changement de cadrage en cours (élargir / resserrer la vue satellite).
  const [zoomEnCours, setZoomEnCours] = useState(false);
  // Repères Sat Premium dessinés sur l'image (bâtiment mesuré + pans).
  const [reperes, setReperes] = useState(true);
  // Coordonnées collées à la main pour aller chercher le bon bâtiment.
  const [coords, setCoords] = useState("");
  // Mode « désigner un bâtiment » : le prochain clic sur la carte crée une
  // section à cet endroit (cabanon, garage détaché, deuxième corps de logis).
  const [modeAjout, setModeAjout] = useState(false);
  // Mesure rechargée que l'on est en train de corriger : on la réenregistre
  // par-dessus au lieu d'en créer une deuxième.
  const [mesureId, setMesureId] = useState("");
  const [clientName, setClientName] = useState("");
  const [clientEmail, setClientEmail] = useState("");
  // Vendeur assigné (forfait Team+) : index dans settings.vendeurs, "" = aucun.
  const [vendeurIdx, setVendeurIdx] = useState("");
  const [quoteLang, setQuoteLang] = useState(lang);
  const [quoteStyle, setQuoteStyle] = useState("detaillee");
  const [choix, setChoix] = useState([]);
  const quoteLangTouched = useRef(false);
  const [sections, setSections] = useState([]);
  const [activeId, setActiveId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggest, setShowSuggest] = useState(false);
  const skipNextFetch = useRef(false);

  // Les 5 soumissions les plus récentes, montrées sous la barre de recherche
  // pour reprendre un dossier sans passer par « Vos estimations ».
  const { data: recentes = [] } = useQuery({
    queryKey: ["estimates"],
    queryFn: async () => (await api.get("/estimates")).data,
  });

  useEffect(() => {
    api.get("/settings").then(({ data }) => {
      setSettings(data);
      setQuoteStyle(data.quote_style === "forfaitaire" ? "forfaitaire" : "detaillee");
      // Présélectionner le premier produit de chaque poste : sans cela une
      // soumission partirait avec un bardeau à 0 $ si l'estimateur oublie le menu.
      setChoix(
        (data.choix || [])
          .filter((g) => (g.options || []).length > 0)
          .map((g) => ({
            groupe: g.nom,
            produit: g.options[0].label,
            couleur: "",
            price_per_square: Number(g.options[0].price_per_square) || 0,
            cost_per_square: Number(g.options[0].cost_per_square) || 0,
            // Sans ce champ, la présélection s'appliquerait à tous les
            // bâtiments, y compris ceux dont le toit ne convient pas.
            toit: g.options[0].toit || "tous",
          }))
      );
    });
  }, []);

  // Default the client quote language to the roofer's current UI language,
  // until the user explicitly overrides it for this estimate.
  useEffect(() => {
    if (!quoteLangTouched.current) setQuoteLang(lang);
  }, [lang]);

  // Garde-fou : si la langue de PDF courante sort de la paire du pays du compte
  // (ex. « fr » restée en mémoire sur un compte américain), on retombe sur une
  // langue valide — l'anglais appartient aux deux paires.
  useEffect(() => {
    if (!settings) return;
    const paire = paireLangues(settings.country);
    if (!paire.includes(quoteLang)) setQuoteLang(paire.includes(lang) ? lang : "en");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [settings, quoteLang]);

  // Le guide n'apparaît qu'avec l'image : hors satellite (saisie manuelle) il
  // n'y a rien à tracer, donc rien à expliquer.
  //
  // Et on laisse Sat Premium répondre avant de choisir QUEL guide montrer : la
  // mesure arrive 1,2 s après la stabilisation du contour, plus le réseau.
  // Ouvrir tout de suite reviendrait à expliquer les trois gestes du tracé à
  // quelqu'un qui, deux secondes plus tard, n'aura plus rien à tracer.
  const sectionsRef = useRef(sections);
  sectionsRef.current = sections;
  useEffect(() => {
    if (!result || result.manuel) return;
    const tm = setTimeout(() => {
      const sec = sectionsRef.current.find((x) => x.id === activeId) || sectionsRef.current[0];
      const v = sec && sec.useSolar && sec.solar?.disponible ? "satellite" : "trace";
      if (guideTraceAVoir(v)) { setGuideVariante(v); setGuideTrace(true); }
    }, 2400);
    return () => clearTimeout(tm);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [result]);

  // Lien profond depuis la notification « prête à valider » :
  // /new?adresse=…&nom=…&courriel=… préremplit le dossier et lance l'analyse
  // tout seul — le responsable n'a qu'à vérifier le contour puis générer le PDF.
  // Et /new?mesure=<id> charge une mesure déjà enregistrée : AUCUN appel
  // satellite, rien de décompté — la mesure a déjà été payée.
  const autoLance = useRef(false);
  const mapBoxRef = useRef(null);
  useEffect(() => {
    if (!settings || autoLance.current) return;
    const q = new URLSearchParams(window.location.search);
    const mesureId = (q.get("mesure") || "").trim();
    if (mesureId) {
      autoLance.current = true;
      chargerMesure(mesureId);
      return;
    }
    const adresse = (q.get("adresse") || "").trim();
    if (!adresse) return;
    autoLance.current = true;
    skipNextFetch.current = true;
    setAddress(adresse);
    if (q.get("nom")) setClientName(q.get("nom"));
    if (q.get("courriel")) setClientEmail(q.get("courriel"));
    analyze(null, adresse);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [settings]);

  // Échap quitte le mode désignation : un curseur qui reste en croix sans
  // qu'on sache comment en sortir est un piège.
  useEffect(() => {
    if (!modeAjout) return;
    const surTouche = (e) => { if (e.key === "Escape") setModeAjout(false); };
    window.addEventListener("keydown", surTouche);
    return () => window.removeEventListener("keydown", surTouche);
  }, [modeAjout]);

  // Reconstruit l'atelier depuis une mesure enregistrée. Les sections mesurées
  // par Sat Premium retrouvent leur surface exacte via un objet solar minimal ;
  // pitchTouched/useSolarTouched empêchent tout écrasement automatique.
  const chargerMesure = async (mesureId) => {
    setAnalyzing(true);
    try {
      const { data: m } = await api.get(`/mesures/${mesureId}`);
      // En mode « mesure » on corrige CETTE mesure ; en mode soumission on s'en
      // sert seulement de point de départ, elle ne doit pas être réécrite.
      if (modeMesure) setMesureId(mesureId);
      skipNextFetch.current = true;
      setAddress(m.property_address || "");
      setResult({
        address: m.property_address, manuel: !!m.manuel, depuisMesure: true,
        lat: m.lat, lng: m.lng, image_base64: m.image_base64 || "",
        polygon: (m.sections?.[0]?.polygon) || [],
        ground_width_m: m.ground_width_m || 0, ground_height_m: m.ground_height_m || 0,
        footprint_sqft: m.footprint_sqft || 0, roof_type: m.roof_type || "gable",
        estimated_pitch: m.pitch_label || "", confidence: m.confidence || 0,
        notes: m.ai_notes || "", sat_provider: m.provider || "",
        solar: { disponible: false }, using_google: true,
      });
      const secs = (m.sections || []).map((sec, i) => {
        const pitch = PITCH_OPTIONS.find((p) => p.label === sec.pitch_label)
          || PITCH_OPTIONS.find((p) => p.value === sec.pitch_multiplier)
          || pitchFromEstimate(m.pitch_label);
        const solaire = sec.mesure === "solar";
        return {
          id: uid(), label: sec.label || `${t("newEst.roofN")} ${i + 1}`,
          polygon: sec.polygon || [], footprint: sec.footprint_sqft || 0,
          pitch, roofType: sec.roof_type || "gable",
          pitchTouched: true, useSolarTouched: true, useSolar: solaire,
          // La réponse Solar est réenregistrée avec la mesure : on la remet
          // telle quelle, donc les pans cochés et le rectangle bleu
          // réapparaissent. Le repli minimal sert aux mesures enregistrées
          // avant qu'on stocke ce détail — elles gardent au moins leur surface.
          solar: sec.solar || (solaire
            ? { disponible: true, surface_toit_pi2: sec.roof_area_sqft || 0, pente_principale: sec.pitch_label }
            : undefined),
          segExclus: sec.seg_exclus || [],
          solarBat: sec.solar_bat || "",
          solarEcart: sec.solar_ecart ?? undefined,
          solarHorsBoite: !!sec.solar_hors_boite,
          saisie: sec.saisie || "",
          longueur: sec.longueur || "",
          largeur: sec.largeur || "",
          forfait: sec.forfait || "",
          coutForfait: sec.cout_forfait || "",
        };
      });
      const liste = secs.length ? secs : [{
        id: uid(), label: t("newEst.roofN") + " 1", polygon: [],
        footprint: m.footprint_sqft || 0, pitch: pitchFromEstimate(m.pitch_label), roofType: m.roof_type || "gable",
      }];
      setSections(liste);
      setActiveId(liste[0].id);
      toast.success(fr ? "Mesure chargée — aucun appel satellite décompté" : "Measurement loaded — no satellite call used");
    } catch {
      toast.error(fr ? "Mesure introuvable" : "Measurement not found");
    } finally {
      setAnalyzing(false);
    }
  };

  // Autocomplétion : chaque appel est facturé par Google. Le seuil (15 caractères)
  // et l'attente (1,5 s) sont volontairement hauts — la suggestion n'arrive qu'une
  // fois l'adresse presque entièrement tapée, et une seule fois plutôt qu'à chaque
  // frappe. Le géocodage corrige de toute façon les fautes, donc on ne perd que du
  // confort. Le serveur applique le même seuil (voir places_autocomplete).
  useEffect(() => {
    if (skipNextFetch.current) { skipNextFetch.current = false; return; }
    if (!address || address.trim().length < AUTOCOMPLETE_MIN) { setSuggestions([]); return; }
    const tm = setTimeout(async () => {
      try {
        const { data } = await api.get("/places/autocomplete", { params: { q: address, country: settings?.country || "ca" } });
        setSuggestions(data.suggestions || []);
      } catch { setSuggestions([]); }
    }, AUTOCOMPLETE_DELAI);
    return () => clearTimeout(tm);
  }, [address, settings]);

  const pickSuggestion = (sug) => {
    skipNextFetch.current = true;
    setAddress(sug);
    setSuggestions([]);
    setShowSuggest(false);
  };

  const analyze = async (e, addrOverride) => {
    e?.preventDefault();
    setShowSuggest(false);
    setSuggestions([]);
    const addr = (addrOverride ?? address).trim();
    if (!addr) return;
    setAnalyzing(true);
    setResult(null);
    try {
      const { data } = await api.post("/analyze-roof", { address: addr, country: settings?.country || "ca" });
      setResult(data);
      const id = uid();
      setSections([{
        id, label: t("newEst.roofN") + " 1", polygon: data.polygon || [],
        footprint: data.footprint_sqft, pitch: PITCH_VIDE, roofType: data.roof_type,
      }]);
      setActiveId(id);
      if (!data.using_google) toast.info(t("newEst.freeImagery"));
      toast.success(t("newEst.analyzed"));
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || t("newEst.analyzeFail"));
    } finally {
      setAnalyzing(false);
    }
  };

  // Mesures saisies à la main : le couvreur a monté sur le toit, ou il a les
  // chiffres d'un rapport d'assurance. Aucun appel satellite, donc AUCUNE
  // estimation décomptée de son forfait — c'est tout l'intérêt.
  const lancerManuel = () => {
    const addr = address.trim();
    if (!addr) { toast.error(t("newEst.addressPlaceholder")); return; }
    setShowSuggest(false);
    setSuggestions([]);
    setResult({
      address: addr, manuel: true,
      lat: null, lng: null, image_base64: "", polygon: [],
      ground_width_m: 0, ground_height_m: 0,
      footprint_sqft: 0, roof_type: "gable", estimated_pitch: "6/12",
      confidence: 1, notes: "", sat_provider: "", solar: { disponible: false },
      using_google: true,
    });
    const id = uid();
    setSections([{
      id, label: t("newEst.roofN") + " 1", polygon: [],
      footprint: 0, pitch: PITCH_VIDE, roofType: "gable",
    }]);
    setActiveId(id);
  };

  // Vue 3D Google Maps en pop-up (double de la taille de l'image affichée) :
  // en inclinant la vue, on voit immédiatement si le toit est plat ou en pente.
  const ouvrirPente3D = () => {
    if (!result) return;
    const url = `https://www.google.com/maps/@${result.lat},${result.lng},70m/data=!3m1!1e3`;
    const r = mapBoxRef.current ? mapBoxRef.current.getBoundingClientRect() : null;
    const w = Math.min(Math.round((r && r.width ? r.width : 600) * 2), window.screen.availWidth - 40);
    const h = Math.min(Math.round((r && r.height ? r.height : 600) * 2), window.screen.availHeight - 60);
    window.open(url, "pente3d", `width=${w},height=${h},left=60,top=40,noopener`);
  };

  // Zoom réellement représenté par l'image affichée. Une mesure rechargée ne
  // porte pas ce champ ; sa largeur au sol, elle, est enregistrée — et c'est
  // elle qui fait foi. Sans ça, une mesure sauvegardée en vue large se croirait
  // au zoom 20 et le bouton « Resserrer » resterait caché.
  const zoomActuel = () => {
    if (result?.zoom) return result.zoom;
    const g = result?.ground_width_m || 0;
    if (!g || result?.lat == null) return 20;
    const mppMax = 156543.03392 * Math.cos((result.lat * Math.PI) / 180);
    return Math.min(20, Math.max(18, Math.round(Math.log2((mppMax * 640) / g))));
  };

  // Élargir ou resserrer la vue satellite sans relancer la mesure.
  //
  // Au zoom 20 l'image couvre environ 66 m de côté. Sur un terrain de campagne,
  // les cabanons sont souvent plus loin que ça : ils ne sont pas seulement mal
  // placés, ils sont ABSENTS de l'image, donc impossibles à tracer. Chaque
  // palier double la largeur au sol (66 m → 133 m → 266 m).
  //
  // Aucune estimation décomptée : c'est la même mesure, regardée de plus loin.
  // Recadrage : nouveau centre et/ou nouveau zoom, sans relancer la mesure.
  //
  // Le logiciel ne peut pas deviner quelle maison est la bonne — au 86 rue du
  // Midi, l'adresse tombe sur la rue et le satellite rend la maison du voisin.
  // Le couvreur, lui, la reconnaît. Il lui faut donc le moyen d'aller la
  // chercher : se déplacer, s'éloigner, ou coller des coordonnées relevées
  // dans Google Maps.
  //
  // Les contours sont stockés en fractions de l'image. On les repasse par leurs
  // vraies coordonnées avant de les replacer dans le nouveau cadre : sans ça un
  // toit tracé suivrait bêtement l'image et changerait de surface.
  //
  // `poserContour` : le couvreur ne s'est pas contenté de regarder ailleurs, il
  // a DÉSIGNÉ un bâtiment (coordonnées collées, ou bouton « Poser le contour
  // ici »). On y pose alors le contour de la section courante, ce qui déclenche
  // une nouvelle mesure Sat Premium à cet endroit. Sans ça, la vue se centrait
  // sur la bonne maison mais la mesure restait celle de l'ancienne — le pire
  // des deux mondes.
  const recadrer = async (lat, lng, zoom, poserContour = false) => {
    if (!result || result.manuel || zoomEnCours) return;
    setZoomEnCours(true);
    try {
      const { data } = await api.post("/satellite-view", { lat, lng, zoom });
      const avant = result;
      const apres = { ...result, lat, lng, image_base64: data.image_base64, zoom: data.zoom,
                      ground_width_m: data.ground_width_m, ground_height_m: data.ground_height_m };
      setSections((list) => list.map((sec) => {
        if (poserContour && sec.id === activeId) {
          const neuf = DEFAULT_RECT.map((pt) => [...pt]);
          return { ...sec, polygon: neuf,
                   footprint: footprintFromPolygon(neuf, apres.ground_width_m, apres.ground_height_m) };
        }
        const polygon = (sec.polygon || []).map(([x, y]) => {
          const [pLat, pLng] = fractionVersLatLng(x, y, avant);
          return latLngVersFraction(pLat, pLng, apres);
        });
        return { ...sec, polygon,
                 footprint: footprintFromPolygon(polygon, apres.ground_width_m, apres.ground_height_m) };
      }));
      // Même raison qu'à la création d'un bâtiment : poser le contour ou coller
      // des coordonnées est une désignation, elle doit pouvoir être mesurée.
      setResult(poserContour && apres.depuisMesure ? { ...apres, depuisMesure: false } : apres);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || t("newEst.analyzeFail"));
    } finally {
      setZoomEnCours(false);
    }
  };

  const changerZoom = (zoom) => recadrer(result.lat, result.lng, zoom);

  // Déplacement d'un demi-cadre : assez pour découvrir du terrain neuf sans
  // perdre de vue ce qu'on regardait.
  const deplacer = (dx, dy) => {
    if (!result) return;
    const lat = result.lat - (dy * 0.5 * result.ground_height_m) / 111320;
    const lng = result.lng + (dx * 0.5 * result.ground_width_m) / (111320 * Math.cos((result.lat * Math.PI) / 180));
    recadrer(lat, lng, zoomActuel());
  };

  // Coordonnées collées depuis Google Maps : « 45.906517, -74.228461 », avec ou
  // sans virgule, avec ou sans parenthèses.
  const allerAuxCoordonnees = (texte) => {
    const n = (texte || "").match(/-?\d+(?:[.,]\d+)?/g);
    if (!n || n.length < 2) { toast.error(t("newEst.badCoords")); return; }
    const lat = parseFloat(n[0].replace(",", ".")), lng = parseFloat(n[1].replace(",", "."));
    if (!isFinite(lat) || !isFinite(lng) || Math.abs(lat) > 90 || Math.abs(lng) > 180) {
      toast.error(t("newEst.badCoords")); return;
    }
    setCoords("");
    // Resserre aussi : après l'élargissement automatique, la maison désignée
    // n'occuperait que quelques pixels dans un cadre de 266 m.
    recadrer(lat, lng, 20, true);
  };

  const active = sections.find((sec) => sec.id === activeId);

  // Mesure Solar PAR STRUCTURE : quand le contour de la section active se stabilise
  // (1,2 s), on interroge Google au centre de ce contour — maison ET cabanon
  // reçoivent chacun leur pente et surface mesurées. La pente auto ne s'applique
  // que si l'utilisateur n'y a pas touché et qu'un nouveau bâtiment est trouvé.
  useEffect(() => {
    // Mesure rechargée : les surfaces sont déjà arrêtées, on ne réinterroge pas.
    if (!result || !active || result.depuisMesure) return;
    const pts = active.polygon || [];
    if (pts.length < 3) return;
    const tm = setTimeout(async () => {
      try {
        const cx = pts.reduce((a, p) => a + p[0], 0) / pts.length;
        const cy = pts.reduce((a, p) => a + p[1], 0) / pts.length;
        const lat = result.lat - ((cy - 0.5) * result.ground_height_m) / 111320;
        const lng = result.lng + ((cx - 0.5) * result.ground_width_m) / (111320 * Math.cos((result.lat * Math.PI) / 180));
        // Chaque appel Solar est facturé (10 $ les 1 000). L'effet se relance à
        // chaque fois que le couvreur lâche une poignée : ajuster un contour
        // déclenchait donc jusqu'à une dizaine d'appels pour le MÊME bâtiment,
        // qui renvoie évidemment la même réponse. En deçà de quelques mètres,
        // « le bâtiment le plus proche » ne change pas — on garde la réponse.
        const dejaVu = active.solarPoint;
        if (dejaVu && distanceM(lat, lng, dejaVu.lat, dejaVu.lng) < SOLAR_MEME_POINT_M) return;
        const { data } = await api.post("/solar-at-point", { lat, lng });
        if (!data || !data.disponible) return;
        const bat = data.centre && data.centre.lat != null ? `${data.centre.lat},${data.centre.lng}` : "";
        setSections((list) => {
          // Solar renvoie le bâtiment LE PLUS PROCHE : si une autre section pointe
          // déjà sur ce même bâtiment (ex. cabanon absent de la base Solar), on ne
          // prend pas sa mesure par défaut — ce serait la surface du voisin.
          const dejaPris = list.some((s) => s.id !== active.id && s.solarBat === bat);
          return list.map((sec) => {
            if (sec.id !== active.id) return sec;
            // ── Le satellite a-t-il rendu le bâtiment qu'on lui montrait ? ──
            //
            // `findClosest` rend le plus PROCHE, pas le bon. Deux contrôles,
            // parce qu'aucun des deux ne suffit seul :
            //
            //  · la distance, pour le cas du 86 rue du Midi — adresse tombée
            //    sur la rue, bâtiment rendu à 28,7 m, c'était celui du voisin ;
            //  · le point visé DANS le rectangle mesuré, pour le petit cabanon
            //    désigné au clic : un bâtiment voisin à une dizaine de mètres
            //    passe sous le seuil de distance alors qu'il est manifestement
            //    ailleurs. Un cabanon fait 3 m de large, une maison 15 : le même
            //    écart est aberrant pour l'un et normal pour l'autre.
            const ecart = bat ? distanceM(lat, lng, data.centre.lat, data.centre.lng) : 0;
            const horsBoite = Boolean(bat) && !pointDansBoite(lat, lng, data.boite);
            const douteux = ecart >= SOLAR_ECART_DOUTEUX_M || horsBoite;

            const patch = { solar: data, solarBat: bat, solarPoint: { lat, lng },
                            solarEcart: Math.round(ecart), solarHorsBoite: horsBoite };
            // Nouveau bâtiment : on repart de tous les pans cochés.
            if (bat && sec.solarBat !== bat) patch.segExclus = [];
            // Le contour épouse le rectangle mesuré — mais SEULEMENT si la
            // mesure est crédible. Sinon il sauterait sur le bâtiment que Solar
            // a rendu, c'est-à-dire loin de l'endroit visé, et le couvreur
            // devrait le ramener à la main. Douteux : il reste où on a cliqué,
            // prêt à être tracé.
            if (bat && sec.solarBat !== bat && data.boite && !douteux) {
              patch.polygon = boiteEnPolygone(data.boite);
              patch.footprint = footprintFromPolygon(
                patch.polygon, result.ground_width_m, result.ground_height_m);
            }
            if (!sec.pitchTouched && bat && sec.solarBat !== bat && !douteux) {
              patch.pitch = pitchFromEstimate(data.pente_principale);
            }
            // Forme du toit : Solar la déduit des azimuts de chaque pan, donc
            // d'une mesure. Gemini, lui, regarde une photo du dessus où une
            // croupe et deux versants se ressemblent beaucoup. On ne l'écrase
            // jamais si le couvreur y a touché.
            if (!sec.roofTypeTouched && bat && sec.solarBat !== bat && !douteux
                && data.forme && data.forme !== "unknown") {
              patch.roofType = data.forme;
            }
            if (!sec.useSolarTouched) patch.useSolar = Boolean(bat) && !dejaPris && !douteux;
            return { ...sec, ...patch };
          });
        });
      } catch { /* hors couverture ou réseau : on garde l'estimation en place */ }
    }, 1200);
    return () => clearTimeout(tm);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [result, activeId, JSON.stringify(active && active.polygon)]);

  const handlePolygonChange = (next) => {
    if (!result) return;
    setSections((list) => list.map((sec) =>
      sec.id === activeId ? { ...sec, polygon: next, footprint: footprintFromPolygon(next, result.ground_width_m, result.ground_height_m) } : sec
    ));
  };

  const updateActive = (patch) => setSections((list) => list.map((sec) => (sec.id === activeId ? { ...sec, ...patch } : sec)));

  // Repères Sat Premium : les rectangles que Google renvoie, ramenés dans le
  // repère de l'image (fractions 0-1). C'est l'inverse exact du calcul qui sert
  // à interroger Solar au centre d'un contour, plus bas.
  //
  // Ils existent pour une seule raison : `findClosest` renvoie le bâtiment le
  // plus PROCHE, pas forcément le bon. Sur un grand terrain ou une adresse mal
  // géocodée — 28,7 m d'écart au 86 rue du Midi — il peut servir la maison du
  // voisin, avec une surface parfaitement plausible. Sans rectangle sur
  // l'image, rien ne le montre.
  const fractionVersLatLng = (cx, cy, r) => [
    r.lat - ((cy - 0.5) * r.ground_height_m) / 111320,
    r.lng + ((cx - 0.5) * r.ground_width_m) / (111320 * Math.cos((r.lat * Math.PI) / 180)),
  ];
  const latLngVersFraction = (lat, lng, r) => [
    0.5 + ((lng - r.lng) * 111320 * Math.cos((r.lat * Math.PI) / 180)) / r.ground_width_m,
    0.5 - ((lat - r.lat) * 111320) / r.ground_height_m,
  ];
  const versFraction = (lat, lng) => latLngVersFraction(lat, lng, result);
  // Le point visé tombe-t-il dans le bâtiment rendu par Solar ?
  //
  // Bien meilleur critère qu'une distance : un cabanon fait 3 m de large, une
  // maison 15 — le même écart de 10 m est aberrant pour l'un et normal pour
  // l'autre. Si le point cliqué n'est pas DANS le rectangle mesuré, c'est que
  // le satellite a rendu autre chose que ce qu'on lui montrait du doigt.
  // La marge couvre l'imprécision du clic et celle du rectangle lui-même.
  const MARGE_BOITE_M = 3;
  const pointDansBoite = (lat, lng, b) => {
    if (!b) return true;                       // pas de rectangle : rien à vérifier
    const dLat = MARGE_BOITE_M / 111320;
    const dLng = MARGE_BOITE_M / (111320 * Math.cos((lat * Math.PI) / 180));
    return lat >= b.sud - dLat && lat <= b.nord + dLat
        && lng >= b.ouest - dLng && lng <= b.est + dLng;
  };

  const boiteEnPolygone = (b) => {
    const [x1, y1] = versFraction(b.nord, b.ouest);
    const [x2, y2] = versFraction(b.sud, b.est);
    return [[x1, y1], [x2, y1], [x2, y2], [x1, y2]];
  };
  const reperesSolar = () => {
    if (!reperes || !result || result.manuel || !result.ground_width_m || result.lat == null) return [];
    const out = [];
    // Un rectangle par bâtiment mesuré, toutes sections confondues : le couvreur
    // voit d'un coup lesquels sont pris et lesquels manquent.
    sections.forEach((sec) => {
      if (sec.useSolar && sec.solar?.boite) {
        out.push({ boite: boiteEnPolygone(sec.solar.boite), principal: sec.id === activeId });
      }
    });
    // Les pans, seulement pour la section en cours — sinon l'image devient
    // illisible dès qu'il y a deux bâtiments.
    segmentsRetenus(active || {}).forEach((seg) => {
      if (seg.boite) out.push({ boite: boiteEnPolygone(seg.boite), principal: false });
    });
    return out;
  };

  // Une section mesurée par Sat Premium n'a plus rien à tracer : sa surface
  // vient de Solar, le contour orange ne compte dans aucun calcul. Le laisser
  // modifiable au-dessus du bâtiment ne fait qu'inviter à un geste inutile, et
  // il masque le rectangle mesuré. On le retire — décocher « Utiliser la mesure
  // Sat Premium » le fait revenir, sur le rectangle du bâtiment.
  const mesureParSolar = (sec) => Boolean(sec && sec.useSolar && sec.solar?.disponible);

  // Cocher / décocher un pan du toit mesuré par Solar.
  const basculerSegment = (segId) => setSections((list) => list.map((sec) => {
    if (sec.id !== activeId) return sec;
    const exclus = sec.segExclus || [];
    const suivant = exclus.includes(segId) ? exclus.filter((x) => x !== segId) : [...exclus, segId];
    const retenus = (sec.solar?.segments || []).filter((s) => !suivant.includes(s.id));
    const patch = { segExclus: suivant };
    // La pente affichée suit le plus grand pan encore retenu (les segments
    // arrivent triés du plus grand au plus petit) : décocher le pan principal
    // ne doit pas laisser sa pente sur la soumission.
    if (!sec.pitchTouched && retenus.length) patch.pitch = pitchFromEstimate(retenus[0].pente);
    return { ...sec, ...patch };
  }));

  // Ajouter un bâtiment : on n'en pose plus un au milieu de l'image en espérant
  // qu'il tombe juste. Le couvreur VISE la construction, et le satellite fait le
  // reste — ou pas, et il la trace lui-même.
  const addRoof = () => {
    if (!result) return;
    if (result.manuel) { creerSectionA(0.5, 0.5); return; }
    setModeAjout(true);
    toast.info(t("newEst.aimAtBuilding"));
  };

  // Bâtiment saisi aux dimensions, sans passage par la carte.
  //
  // Certains cabanons ne sont tout simplement pas traçables : trop petits pour
  // le satellite, ou cachés sous les arbres. Le couvreur a les mesures — son
  // client les lui a données au téléphone. Il les entre, et il peut y coller un
  // prix rond plutôt que de faire courir des carrés et des pertes sur un
  // ouvrage qu'il chiffre de tête.
  const ajouterDimensions = () => {
    const id = uid();
    setSections((list) => [...list, {
      id, label: list.length === 1 ? t("newEst.cabananLabel") : `${t("newEst.roofN")} ${list.length + 1}`,
      saisie: "dimensions", longueur: "", largeur: "", forfait: "", coutForfait: "",
      polygon: [], footprint: 0, pitch: PITCH_VIDE, roofType: "structure",
      pitchTouched: true, useSolarTouched: true,
    }]);
    setActiveId(id);
    setModeAjout(false);
    toast.success(t("newEst.dimsAdded"));
  };

  // Crée une section centrée sur le point visé.
  //
  // Le contour de départ fait six mètres de côté : à la taille d'un vrai
  // cabanon. À dix, un contour laissé tel quel ajoutait 1 200 pi² fantômes au
  // total quand le couvreur oubliait de l'ajuster. Assez petit pour ne pas
  // déborder sur le bâtiment voisin — c'est son CENTRE qui interroge le
  // satellite — et assez grand pour rester saisissable au doigt.
  //
  // ⚠️ Rien n'est touché sur les sections déjà mesurées : la nouvelle devient
  // simplement active, et l'effet Solar ne modifie jamais que la section
  // active. La mesure du bâtiment principal reste donc telle quelle.
  const creerSectionA = (fx, fy) => {
    if (!result) return;
    const demi = result.ground_width_m ? Math.min(0.25, 3 / result.ground_width_m) : 0.08;
    const demiY = result.ground_height_m ? Math.min(0.25, 3 / result.ground_height_m) : 0.08;
    const clamp = (v) => Math.min(1, Math.max(0, v));
    const polygon = [
      [clamp(fx - demi), clamp(fy - demiY)], [clamp(fx + demi), clamp(fy - demiY)],
      [clamp(fx + demi), clamp(fy + demiY)], [clamp(fx - demi), clamp(fy + demiY)],
    ];
    const id = uid();
    setSections((list) => [...list, {
      id, label: list.length === 1 ? t("newEst.cabananLabel") : `${t("newEst.roofN")} ${list.length + 1}`,
      polygon, footprint: footprintFromPolygon(polygon, result.ground_width_m, result.ground_height_m),
      pitch: PITCH_VIDE, roofType: "structure",
    }]);
    setActiveId(id);
    setModeAjout(false);
    // Une mesure rechargée n'interroge plus le satellite — c'est tout l'intérêt
    // de la rouvrir. Mais désigner un bâtiment est une demande explicite : on
    // rouvre alors la porte, sinon le cabanon ajouté ne serait jamais mesuré.
    setResult((r) => (r && r.depuisMesure ? { ...r, depuisMesure: false } : r));
    toast.success(t("newEst.secondAdded"));
  };

  const removeSection = (id) => {
    if (sections.length <= 1) return;
    const next = sections.filter((sec) => sec.id !== id);
    setSections(next);
    if (activeId === id) setActiveId(next[0].id);
  };

  const resetActivePolygon = () => {
    if (!active || active.id !== sections[0]?.id) { toast.info(t("newEst.resetOnlyMain")); return; }
    updateActive({ polygon: result.polygon || [], footprint: result.footprint_sqft });
    toast.info(t("newEst.resetDone"));
  };

  // Pans du toit retenus dans le calcul. Solar découpe le toit en segments et
  // leur somme redonne EXACTEMENT le toit complet (vérifié : écart 0,0 %) —
  // c'est ce qui permet de décocher un pan sans fausser le total. Un couvreur
  // qui refait la maison mais pas l'aile du garage n'avait jusqu'ici que le
  // tout ou rien.
  const segmentsSolar = (sec) => (sec.useSolar && sec.solar?.disponible && sec.solar.segments) || [];
  const segmentsRetenus = (sec) =>
    segmentsSolar(sec).filter((seg) => !(sec.segExclus || []).includes(seg.id));

  // Surface de toit d'une section : la mesure Solar (pente déjà incluse) quand
  // elle est retenue, sinon le polygone tracé multiplié par la pente.
  const aireSection = (sec) => {
    // Saisie aux dimensions : le client a donné 8 pi sur 12, point. Aucun
    // contour, aucune présence sur l'image — le cabanon est trop petit ou trop
    // caché sous un arbre pour que le satellite en montre quoi que ce soit.
    if (sec.saisie === "dimensions") {
      return (Number(sec.longueur) || 0) * (Number(sec.largeur) || 0) * sec.pitch.value;
    }
    if (sec.useSolar && sec.solar?.disponible) {
      // Mesure rechargée ou réponse Solar sans détail par pan : total global.
      if (!segmentsSolar(sec).length) return Number(sec.solar.surface_toit_pi2) || 0;
      return segmentsRetenus(sec).reduce((a, seg) => a + (Number(seg.surface_toit_pi2) || 0), 0);
    }
    return sec.footprint * sec.pitch.value;
  };

  // Surface AU SOL d'une section. Sur une section mesurée par Solar, elle vient
  // aussi de Solar : le polygone tracé n'a alors servi qu'à désigner le
  // bâtiment, pas à le mesurer, et l'afficher fausserait le relevé.
  const solSection = (sec) => {
    if (sec.saisie === "dimensions") {
      return (Number(sec.longueur) || 0) * (Number(sec.largeur) || 0);
    }
    if (sec.useSolar && sec.solar?.disponible && segmentsSolar(sec).length) {
      return segmentsRetenus(sec).reduce((a, seg) => a + (Number(seg.surface_sol_pi2) || 0), 0);
    }
    return sec.footprint;
  };

  // Bâtiments dont la pente n'a été ni mesurée ni choisie. Tant qu'il en reste
  // un, rien ne s'enregistre : une soumission partie sur une pente supposée se
  // découvre au chantier, quand le prix est déjà donné.
  const sansPente = sections.filter((sec) => !pitchDefinie(sec.pitch));

  const roofAreaTotal = sections.reduce((a, sec) => a + aireSection(sec), 0);
  const totalFootprint = sections.reduce((a, sec) => a + solSection(sec), 0);
  // Le bâtiment principal décide du type de toit : c'est lui qui porte le
  // système de toiture retenu pour toute la soumission.
  // Chaque bâtiment est chiffré avec SON type de toit : le cabanon à membrane
  // ne suit plus les taux de la maison en bardeau. Le profit et la taxe restent
  // calculés une seule fois sur l'ensemble.
  const batiments = sections.map((sec) => ({
    roofAreaSqft: aireSection(sec), plat: estToitPlat(sec.pitch?.value),
    label: sec.label,
    forfait: Number(sec.forfait) || 0,
    coutForfait: Number(sec.coutForfait) || 0,
  }));
  // Types de toit présents sur le chantier : le catalogue doit proposer les
  // produits des DEUX si la propriété a les deux.
  const platsPresents = [...new Set(batiments.map((b) => b.plat))];
  const calc = settings ? computeEstimateBatiments({ batiments, settings, choix, lang: quoteLang }) : null;
  // Ce que le récapitulatif affiche : une ligne par bâtiment, dans l'ordre.
  const batimentsRecap = sections.map((sec) => ({
    id: sec.id, label: sec.label,
    aire: aireSection(sec), sol: solSection(sec),
    satellite: mesureParSolar(sec), pitchLabel: sec.pitch.label,
    dimensions: sec.saisie === "dimensions",
    forfait: Number(sec.forfait) || 0,
  }));

  const sectionsBD = sections.map((sec) => ({
    label: sec.label, footprintSqft: solSection(sec),
    // Pente pas encore choisie : on n'écrit ni « · IA » ni « · Sat Premium »
    // sous un libellé vide — il n'y a rien à créditer.
    pitchLabel: !pitchDefinie(sec.pitch) ? "—"
      : sec.useSolar && sec.solar?.disponible ? `${sec.pitch.label} · Sat Premium` : `${sec.pitch.label} · IA`,
    roofArea: aireSection(sec),
  }));

  // Même forme dans une mesure seule et dans une soumission : c'est ce qui
  // permet de recharger l'une dans l'autre sans conversion.
  const sectionsEnPayload = () => sections.map((sec) => ({
    label: sec.label, polygon: sec.polygon, footprint_sqft: solSection(sec),
    pitch_label: sec.pitch.label, pitch_multiplier: sec.pitch.value,
    roof_area_sqft: aireSection(sec), roof_type: sec.roofType || "",
    mesure: sec.useSolar && sec.solar?.disponible ? "solar" : "trace",
    // Ce qu'il faut pour rouvrir le dossier à l'identique, sans redemander quoi
    // que ce soit au satellite.
    //
    // ⚠️ On garde la réponse Solar même quand elle N'A PAS été retenue. Sinon,
    // un bâtiment tracé à la main revenait sans elle : le couvreur qui voulait
    // finalement la comparer devait relancer une mesure — et repayer un appel
    // pour une réponse qu'on avait déjà eue. Ce qui a été CHOISI est dit par
    // `mesure`, pas par la présence de la donnée.
    solar: sec.solar?.disponible ? sec.solar : undefined,
    seg_exclus: sec.segExclus || [],
    solar_bat: sec.solarBat || "",
    // Les doutes relevés à la mesure repartent avec elle : sans eux, rouvrir un
    // relevé remettrait la case Sat Premium sous les yeux du couvreur sans lui
    // rappeler POURQUOI il l'avait refusée.
    solar_ecart: sec.solarEcart ?? null,
    solar_hors_boite: !!sec.solarHorsBoite,
    // Bâtiment saisi aux dimensions : il n'a ni contour ni mesure satellite,
    // seules ces valeurs le décrivent.
    saisie: sec.saisie || "",
    longueur: Number(sec.longueur) || 0,
    largeur: Number(sec.largeur) || 0,
    forfait: Number(sec.forfait) || 0,
    cout_forfait: Number(sec.coutForfait) || 0,
  }));

  // Enregistrement d'une mesure seule : le relevé, rien d'autre.
  const saveMesure = async () => {
    if (!result || sections.length === 0) return;
    if (sansPente.length) { toast.error(t("newEst.pitchBlock")); return; }
    setSaving(true);
    try {
      const payload = {
        property_address: result.address, lat: result.lat, lng: result.lng,
        // L'image n'est PLUS enregistrée : 1,2 Mo par relevé, soit mille fois la
        // donnée utile. Le serveur la refait au besoin à partir des coordonnées
        // et de la largeur au sol, sans redemander de mesure satellite.
        provider: result.sat_provider,
        ground_width_m: result.ground_width_m, ground_height_m: result.ground_height_m,
        footprint_sqft: totalFootprint, roof_area_sqft: roofAreaTotal,
        roof_type: sections[0].roofType || result.roof_type,
        pitch_label: sections.length > 1 ? `${sections.length} buildings` : sections[0].pitch.label,
        confidence: result.confidence, ai_notes: result.notes,
        manuel: !!result.manuel, sections: sectionsEnPayload(),
      };
      if (mesureId) await api.put(`/mesures/${mesureId}`, payload);
      else await api.post("/mesures", payload);
      queryClient.invalidateQueries({ queryKey: ["mesures"] });
      toast.success(mesureId
        ? (fr ? "Mesure mise à jour" : "Measurement updated")
        : (fr ? "Mesure enregistrée" : "Measurement saved"));
      navigate("/mesures");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || t("newEst.saveFail"));
    } finally {
      setSaving(false);
    }
  };

  const save = async () => {
    if (!result || !calc || sections.length === 0) return;
    if (sansPente.length) { toast.error(t("newEst.pitchBlock")); return; }
    setSaving(true);
    try {
      const sectionsPayload = sectionsEnPayload();
      const payload = {
        property_address: result.address, client_name: clientName, client_email: clientEmail, quote_language: quoteLang,
        lat: result.lat, lng: result.lng, provider: result.sat_provider,
        // Idem pour la soumission : c'est le PDF envoyé au client qui fait
        // archive, pas notre base. Un devis vit quelques mois de toute façon.
        polygon: sections[0].polygon,
        ground_width_m: result.ground_width_m, ground_height_m: result.ground_height_m,
        footprint_sqft: totalFootprint, roof_type: sections[0].roofType || result.roof_type,
        pitch_label: sections.length > 1 ? `${sections.length} buildings` : sections[0].pitch.label,
        pitch_multiplier: sections[0].pitch.value, confidence: result.confidence, ai_notes: result.notes,
        roof_area_sqft: calc.roofArea, total_squares: calc.totalSquares, adjusted_squares: calc.adjustedSquares,
        materials_cost: calc.materialsCost, labor_cost: calc.laborCost, subtotal: calc.subtotal,
        tax: calc.tax, total: calc.total, settings_snapshot: settings, line_items: calc.lineItems,
        quote_style: quoteStyle, cost_total: calc.costTotal || 0, profit: calc.profit || 0,
        choix, sections: sectionsPayload,
        // Vendeur figé sur la soumission, comme les prix. ⚠️ "" = aucun —
        // Number("") vaudrait 0 et assignerait le premier vendeur en douce.
        vendeur_nom: vendeurIdx === "" ? "" : (settings?.vendeurs?.[Number(vendeurIdx)]?.nom || "").trim(),
        vendeur_telephone: vendeurIdx === "" ? "" : (settings?.vendeurs?.[Number(vendeurIdx)]?.telephone || "").trim(),
      };
      const { data } = await api.post("/estimates", payload);
      // Sans cette invalidation, le tableau de bord ressert sa liste en cache :
      // la soumission qu'on vient d'enregistrer semble avoir disparu.
      queryClient.invalidateQueries({ queryKey: ["estimates"] });
      toast.success(t("newEst.saved"));
      navigate(`/estimate/${data.estimate_id}`);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || t("newEst.saveFail"));
    } finally {
      setSaving(false);
    }
  };

  const field = "bg-field border-line-strong rounded-md focus:border-orange-500 focus-visible:ring-orange-500";

  // Barrage : pas de prix, pas de soumission. Une soumission chiffrée à 0 $
  // partirait chez un vrai client — on bloque la page entière (y compris
  // « transformer une mesure ») tant que le couvreur n'a pas mis SES prix.
  // Les mesures, elles, restent libres : mesurer ne demande aucun prix.
  if (!modeMesure && settings && !prixConfigures(settings)) {
    return (
      <Layout>
        <div className="p-6 md:p-10 max-w-3xl">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-brand-ink mb-2">{t("nav.quotes")}</p>
          <h1 className="font-heading text-3xl sm:text-4xl font-extrabold tracking-tighter mb-8">{t("nav.newEstimate")}</h1>
          <div className="bg-surface border border-amber-500/50 rounded-xl p-10 text-center" data-testid="barrage-prix">
            <AlertTriangle className="h-10 w-10 text-amber-600 mx-auto mb-5" />
            <h2 className="font-heading text-2xl font-bold mb-3">
              {fr ? "Entrez d'abord vos prix" : "Enter your prices first"}
            </h2>
            <p className="text-ink-soft max-w-lg mx-auto mb-8">
              {fr
                ? "Vos postes sont prêts, mais tous les montants sont à zéro : une soumission sortirait à 0 $. Entrez le prix de vos matériaux et votre taux de main-d'œuvre — deux minutes — et cette page s'ouvre."
                : "Your line items are ready, but every amount is at zero: a quote would come out at $0. Enter your material prices and labor rate — two minutes — and this page opens up."}
            </p>
            <Button onClick={() => navigate("/settings/prix")} data-testid="barrage-prix-lien" className="h-12 px-8 bg-orange-600 hover:bg-orange-700 rounded-md font-semibold">
              {fr ? "Entrer mes prix" : "Enter my prices"}
            </Button>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="p-6 md:p-10 max-w-6xl">
        <p className="text-xs font-bold uppercase tracking-[0.2em] text-brand-ink mb-2">
          {modeMesure ? t("nav.mesures") : t("nav.quotes")}
        </p>
        <h1 className="font-heading text-3xl sm:text-4xl font-extrabold tracking-tighter mb-6">
          {modeMesure ? t("nav.newMesures") : t("nav.newEstimate")}
        </h1>

        <form onSubmit={analyze} className="flex flex-col sm:flex-row gap-3 mb-8">
          <div className="relative flex-1">
            <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-ink-faint z-10" />
            <Input
              data-testid="address-input"
              value={address}
              onChange={(e) => { setAddress(e.target.value); setShowSuggest(true); }}
              onFocus={() => setShowSuggest(true)}
              onBlur={() => setTimeout(() => setShowSuggest(false), 150)}
              autoComplete="off"
              placeholder={t("newEst.addressPlaceholder")}
              className={`pl-9 h-12 ${field}`}
            />
            {showSuggest && suggestions.length > 0 && (
              <ul data-testid="address-suggestions" className="absolute z-30 mt-1 w-full bg-surface/95 backdrop-blur-xl border border-line-strong rounded-md overflow-hidden shadow-2xl">
                {suggestions.map((sug, i) => (
                  <li key={i}>
                    <button type="button" data-testid={`suggestion-${i}`} onMouseDown={(e) => { e.preventDefault(); pickSuggestion(sug); }}
                      className="w-full text-left px-4 py-2.5 text-sm text-ink-soft hover:bg-orange-600 hover:text-white transition-colors flex items-center gap-2">
                      <MapPin className="h-3.5 w-3.5 shrink-0 text-brand-ink" />
                      <span className="truncate">{sug}</span>
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
          <Button type="submit" disabled={analyzing} data-testid="analyze-button" className="h-12 px-6 bg-orange-600 hover:bg-orange-700 rounded-md font-semibold">
            {analyzing ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Sparkles className="h-4 w-4 mr-2" /> {t("newEst.analyzeBtn")}</>}
          </Button>
          {/* Deuxième chemin, volontairement discret : il sert quand le couvreur
              a déjà ses chiffres. Aucune estimation décomptée. */}
          <Button type="button" variant="outline" onClick={lancerManuel} disabled={analyzing}
            data-testid="manual-button"
            className="h-12 px-5 border-line-strong rounded-md hover:bg-hover-strong hover:text-ink">
            <Ruler className="h-4 w-4 mr-2" /> {t("newEst.manualBtn")}
          </Button>
        </form>

        {!result && !analyzing && (
          <>
            <div className="bg-surface border border-line rounded-md p-12 text-center">
              <Search className="h-10 w-10 text-ink-faint mx-auto mb-4" />
              <p className="text-ink-faint">{t("newEst.emptyPrompt")}</p>
            </div>

            {/* Les 5 soumissions les plus récentes : reprise rapide d'un dossier. */}
            {!modeMesure && recentes.length > 0 && (
              <div className="mt-6">
                <div className="flex items-center justify-between mb-3">
                  <h2 className="text-xs font-bold uppercase tracking-[0.2em] text-ink-faint flex items-center gap-2">
                    <Clock className="h-3.5 w-3.5" /> {lang !== "en" ? "Soumissions récentes" : "Recent estimates"}
                  </h2>
                  <button
                    onClick={() => navigate("/estimations")}
                    data-testid="voir-toutes-estimations"
                    className="text-xs font-semibold text-brand-ink hover:underline inline-flex items-center gap-1"
                  >
                    {lang !== "en" ? "Tout voir" : "See all"} <ArrowRight className="h-3.5 w-3.5" />
                  </button>
                </div>
                <div className="bg-surface border border-line rounded-md overflow-hidden">
                  {recentes.slice(0, 5).map((e) => (
                    <button
                      key={e.estimate_id}
                      onClick={() => navigate(`/estimate/${e.estimate_id}`)}
                      data-testid={`recente-${e.estimate_id}`}
                      className="w-full flex items-center gap-4 px-5 py-3.5 border-b border-line last:border-0 hover:bg-hover transition-colors text-left"
                    >
                      <MapPin className="h-3.5 w-3.5 text-brand-ink shrink-0" />
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium truncate">{e.property_address}</div>
                        <div className="text-xs text-ink-faint mt-0.5">
                          {e.client_name ? `${e.client_name} · ` : ""}{dateCourte(e.created_at, lang)}
                        </div>
                      </div>
                      <span className="font-heading font-bold text-brand-ink shrink-0">{money(e.total)}</span>
                    </button>
                  ))}
                </div>
              </div>
            )}
          </>
        )}

        {analyzing && (
          <div className="bg-surface border border-line rounded-md p-12 text-center">
            <Loader2 className="h-8 w-8 animate-spin text-brand-ink mx-auto mb-4" />
            <p className="text-ink-soft">{t("newEst.loading")}</p>
          </div>
        )}

        {result && calc && active && (
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
            <div className="lg:col-span-7 space-y-4">
              <div className="flex items-center gap-2 text-sm text-ink-soft">
                <MapPin className="h-4 w-4 text-brand-ink" /> {result.address}
              </div>

              <div className="flex flex-wrap items-center gap-2">
                {sections.map((sec, i) => (
                  <div key={sec.id} className={`flex items-center rounded-md border transition-colors ${sec.id === activeId ? "bg-orange-600 border-orange-600 text-white" : "bg-surface border-line text-ink-soft hover:bg-hover-strong"}`}>
                    <button data-testid={`section-tab-${i}`} onClick={() => setActiveId(sec.id)} className="flex items-center gap-2 pl-3 pr-2 py-1.5 text-xs font-semibold">
                      <Home className="h-3.5 w-3.5" /> {sec.label}
                      <span className={sec.id === activeId ? "text-ink/80" : "text-ink-faint"}>· {Math.round(sec.footprint)} {t("units.sqft")}</span>
                    </button>
                    {sections.length > 1 && (
                      <button data-testid={`section-delete-${i}`} onClick={() => removeSection(sec.id)} className="px-1.5 py-1.5 opacity-70 hover:opacity-100"><X className="h-3.5 w-3.5" /></button>
                    )}
                  </div>
                ))}
                <button data-testid="add-roof-button" onClick={addRoof} className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-md border border-dashed border-line-strong text-ink-soft hover:border-orange-500 hover:text-brand-ink transition-colors">
                  <Plus className="h-3.5 w-3.5" /> {t("newEst.addRoof")}
                </button>
                {/* Pour ce que le satellite ne montrera jamais : un cabanon de
                    8 pi sous un érable. Le client a donné les dimensions. */}
                <button data-testid="add-dims-button" onClick={ajouterDimensions} className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-md border border-dashed border-line-strong text-ink-soft hover:border-orange-500 hover:text-brand-ink transition-colors">
                  <Ruler className="h-3.5 w-3.5" /> {t("newEst.addDims")}
                </button>
              </div>

              {result.manuel ? (
                /* Aucune image satellite à montrer. La plaque prend sa place et
                   dit franchement d'où viennent les chiffres : le couvreur les a
                   entrés lui-même. Toutes les mesures se saisissent dans le
                   panneau de droite, exactement comme si le satellite les avait
                   trouvées — type de toit, surface au sol, pente. */
                <div
                  data-testid="manual-plate"
                  className="aspect-square rounded-md border-2 border-dashed border-line-strong bg-surface flex flex-col items-center justify-center text-center px-8"
                >
                  <div className="relative">
                    <SatelliteDish className="h-14 w-14 text-ink-faint" strokeWidth={1.3} />
                    <Slash className="h-14 w-14 text-ink-faint absolute inset-0" strokeWidth={1.3} />
                  </div>
                  <p className="font-heading font-bold text-lg mt-5">{t("newEst.manualBadge")}</p>
                  <p className="text-sm text-ink-soft mt-2 max-w-sm">{t("newEst.manualHint")}</p>
                </div>
              ) : (
                <>
                  <div ref={mapBoxRef} className="relative">
                    <RoofMap image={result.image_base64}
                      polygon={mesureParSolar(active) ? null : active.polygon}
                      editable={!modeAjout && !mesureParSolar(active)} onPolygonChange={handlePolygonChange}
                      extraPolygons={sections.filter((sec) => sec.id !== activeId && !mesureParSolar(sec)).map((sec) => ({ polygon: sec.polygon }))}
                      guides={reperesSolar()}
                      viser={modeAjout}
                      onReleverPoint={(fx, fy) => {
                        // En mode désignation, le clic crée le bâtiment visé.
                        if (modeAjout) { creerSectionA(fx, fy); return; }
                        // Sinon on remplit le champ sans mesurer : le couvreur
                        // voit ce qu'il a visé et confirme. Un clic qui
                        // déclencherait une mesure facturée serait trop bavard.
                        const [la, ln] = fractionVersLatLng(fx, fy, result);
                        setCoords(`${la.toFixed(6)}, ${ln.toFixed(6)}`);
                      }}
                      className="aspect-square" />
                    {/* Bandeau du mode désignation : sans lui, le curseur seul
                        ne dit pas ce qu'on attend du couvreur. */}
                    {modeAjout && (
                      <div data-testid="bandeau-visee"
                        className="absolute inset-x-2 top-2 z-20 flex items-center gap-2 rounded-lg
                                   bg-surface/95 backdrop-blur border border-cyan-500/40 shadow-carte px-3 py-2">
                        <Crosshair className="h-4 w-4 shrink-0 text-cyan-400" />
                        <span className="text-xs text-ink flex-1">{t("newEst.aimAtBuilding")}</span>
                        <button onClick={() => setModeAjout(false)} data-testid="annuler-visee"
                          className="text-xs text-ink-faint hover:text-ink shrink-0">
                          {t("newEst.aimCancel")}
                        </button>
                      </div>
                    )}
                    <GuideTrace ouvert={guideTrace} variante={guideVariante} onFermer={() => setGuideTrace(false)} />
                  </div>
                  {/* Aller chercher le bon bâtiment. Le satellite se trompe de
                      maison dès que l'adresse tombe sur la rue ; le couvreur,
                      lui, la reconnaît — encore faut-il qu'il puisse s'y rendre. */}
                  <div className="flex flex-wrap items-center gap-2 rounded-md border border-line bg-surface px-3 py-2">
                    <span className="text-[11px] uppercase tracking-widest text-ink-faint mr-1">
                      {t("newEst.moveView")}
                    </span>
                    <div className="grid grid-cols-3 gap-0.5">
                      <span />
                      <button onClick={() => deplacer(0, -1)} disabled={zoomEnCours} data-testid="pan-nord"
                        aria-label={t("newEst.panN")} title={t("newEst.panN")}
                        className="p-1 rounded hover:bg-hover-strong text-ink-soft hover:text-ink disabled:opacity-40">
                        <ChevronUp className="h-3.5 w-3.5" />
                      </button>
                      <span />
                      <button onClick={() => deplacer(-1, 0)} disabled={zoomEnCours} data-testid="pan-ouest"
                        aria-label={t("newEst.panW")} title={t("newEst.panW")}
                        className="p-1 rounded hover:bg-hover-strong text-ink-soft hover:text-ink disabled:opacity-40">
                        <ChevronLeft className="h-3.5 w-3.5" />
                      </button>
                      <span className="flex items-center justify-center text-ink-faint">
                        {zoomEnCours ? <Loader2 className="h-3 w-3 animate-spin" /> : <Crosshair className="h-3 w-3" />}
                      </span>
                      <button onClick={() => deplacer(1, 0)} disabled={zoomEnCours} data-testid="pan-est"
                        aria-label={t("newEst.panE")} title={t("newEst.panE")}
                        className="p-1 rounded hover:bg-hover-strong text-ink-soft hover:text-ink disabled:opacity-40">
                        <ChevronRight className="h-3.5 w-3.5" />
                      </button>
                      <span />
                      <button onClick={() => deplacer(0, 1)} disabled={zoomEnCours} data-testid="pan-sud"
                        aria-label={t("newEst.panS")} title={t("newEst.panS")}
                        className="p-1 rounded hover:bg-hover-strong text-ink-soft hover:text-ink disabled:opacity-40">
                        <ChevronDown className="h-3.5 w-3.5" />
                      </button>
                      <span />
                    </div>
                    <input
                      value={coords}
                      onChange={(e) => setCoords(e.target.value)}
                      onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); allerAuxCoordonnees(coords); } }}
                      data-testid="coords-input"
                      placeholder={t("newEst.coordsPlaceholder")}
                      className="flex-1 min-w-[11rem] bg-field border border-line-strong rounded-md px-2 py-1.5 text-xs text-ink outline-none focus:border-orange-500"
                    />
                    <button onClick={() => allerAuxCoordonnees(coords)} disabled={zoomEnCours || !coords.trim()}
                      data-testid="coords-go"
                      className="px-2.5 py-1.5 rounded-md text-xs font-semibold border border-line-strong text-ink-soft hover:text-ink hover:border-orange-500 disabled:opacity-40">
                      {t("newEst.coordsGo")}
                    </button>
                    {/* Google Maps sert de carte de navigation : clic droit sur
                        la maison → les coordonnées se copient, à recoller ici. */}
                    {/* Après un déplacement, le contour est resté sur SON
                        bâtiment — c'est voulu, il est ancré au sol — mais il
                        peut être hors écran, donc plus saisissable. Ce bouton
                        le repose au milieu de ce qu'on regarde. */}
                    {/* Offert MÊME quand une mesure Sat Premium est en place :
                        c'est justement ce qui permet de la refaire ailleurs
                        quand le satellite a désigné la mauvaise maison. */}
                    <button onClick={() => recadrer(result.lat, result.lng, zoomActuel(), true)}
                      disabled={zoomEnCours} data-testid="contour-ici"
                      className="px-2.5 py-1.5 rounded-md text-xs font-semibold border border-line-strong text-ink-soft hover:text-ink hover:border-orange-500 disabled:opacity-40 whitespace-nowrap">
                      {t("newEst.outlineHere")}
                    </button>
                    <a href={`https://www.google.com/maps/@${result.lat},${result.lng},${zoomActuel()}z/data=!3m1!1e3`}
                      target="_blank" rel="noopener noreferrer" data-testid="ouvrir-maps"
                      className="text-xs text-brand-ink hover:underline whitespace-nowrap">
                      {t("newEst.findOnMaps")}
                    </a>
                  </div>
                  <div className="flex flex-wrap items-center justify-between gap-3">
                    {/* Rien à tracer sur un bâtiment mesuré : on dit pourquoi,
                        et comment reprendre la main si on le souhaite. */}
                    {mesureParSolar(active) ? (
                      <div data-testid="rien-a-tracer" className="flex items-center gap-2 text-xs text-cyan-400">
                        <SatelliteDish className="h-3.5 w-3.5" /> {t("newEst.nothingToTrace")}
                      </div>
                    ) : (
                      <div className="flex items-center gap-2 text-xs text-brand-ink">
                        <Move className="h-3.5 w-3.5" /> {t("newEst.dragHint")}
                      </div>
                    )}
                    <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
                      {/* Cadrage : indispensable dès que le bâtiment à tracer
                          n'est pas la maison elle-même. Voir changerZoom(). */}
                      <div className="flex items-center gap-1.5">
                        <button
                          onClick={() => changerZoom(zoomActuel() - 1)}
                          disabled={zoomEnCours || zoomActuel() <= 18}
                          data-testid="elargir-vue"
                          title={t("newEst.widenHint")}
                          className="flex items-center gap-1.5 text-xs text-ink-soft hover:text-ink transition-colors disabled:opacity-40 disabled:hover:text-ink-soft"
                        >
                          {zoomEnCours ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <ZoomOut className="h-3.5 w-3.5" />}
                          {t("newEst.widen")}
                        </button>
                        {zoomActuel() < 20 && (
                          <button
                            onClick={() => changerZoom(zoomActuel() + 1)}
                            disabled={zoomEnCours}
                            data-testid="resserrer-vue"
                            className="flex items-center gap-1.5 text-xs text-ink-soft hover:text-ink transition-colors disabled:opacity-40"
                          >
                            <ZoomIn className="h-3.5 w-3.5" /> {t("newEst.tighten")}
                          </button>
                        )}
                      </div>
                      {/* Repères Sat Premium : montrent QUEL bâtiment Solar a
                          mesuré. Décochables, parce qu'ils gênent le tracé fin. */}
                      {sections.some((sec) => sec.useSolar && sec.solar?.boite) && (
                        <label className="flex items-center gap-1.5 text-xs text-ink-soft hover:text-ink cursor-pointer">
                          <input type="checkbox" checked={reperes} data-testid="toggle-reperes"
                            onChange={(e) => setReperes(e.target.checked)}
                            className="accent-cyan-500 h-3.5 w-3.5" />
                          {t("newEst.solarGuides")}
                        </label>
                      )}
                      <button onClick={() => { setGuideVariante(mesureParSolar(active) ? "satellite" : "trace"); setGuideTrace(true); }} data-testid="revoir-guide-trace" className="flex items-center gap-1.5 text-xs text-ink-soft hover:text-ink transition-colors">
                        <HelpCircle className="h-3.5 w-3.5" /> {lang === "fr" ? "Revoir le guide" : lang === "es" ? "Ver la guía" : "Show the guide"}
                      </button>
                      <button onClick={ouvrirPente3D} data-testid="check-pitch-3d-button" className="flex items-center gap-1.5 text-xs text-brand-ink hover:text-brand-ink transition-colors font-semibold">
                        <Box className="h-3.5 w-3.5" /> {t("newEst.check3d")}
                      </button>
                      {/* Remettre le contour de l'IA n'a aucun sens quand la
                          surface vient du satellite. */}
                      {!mesureParSolar(active) && (
                        <button onClick={resetActivePolygon} data-testid="reset-polygon-button" className="flex items-center gap-1.5 text-xs text-ink-soft hover:text-ink transition-colors">
                          <RotateCcw className="h-3.5 w-3.5" /> {t("newEst.resetAI")}
                        </button>
                      )}
                    </div>
                  </div>
                </>
              )}
              <div className="flex flex-wrap items-center gap-3 text-xs text-ink-faint">
                {result.manuel ? (
                  <span className="px-2 py-1 bg-hover rounded-md">{t("newEst.manualBadge")}</span>
                ) : (
                  <>
                    <span className="px-2 py-1 bg-hover rounded-md">{t("newEst.confidence")} {(result.confidence * 100).toFixed(0)}%</span>
                    <span className="px-2 py-1 bg-hover rounded-md">{t("newEst.source")}: {result.sat_provider}</span>
                  </>
                )}
                {result.solar?.disponible && (
                  <span data-testid="solar-badge" className="px-2 py-1 bg-orange-500/10 border border-orange-500/30 text-brand-ink rounded-md font-semibold">
                    {t("newEst.solarBadge")} : {result.solar.pente_principale === "flat" ? t("newEst.solarFlat") : result.solar.pente_principale}
                    {" · "}{Number(result.solar.surface_toit_pi2).toLocaleString()} {t("units.sqft")}
                  </span>
                )}
              </div>
              {/* Le satellite a rendu un bâtiment loin de l'adresse : le dire
                  franchement. Une surface fausse mais plausible ne se détecte
                  pas — c'est le seul avertissement que le couvreur recevra. */}
              {result.ecart_batiment_m >= 20 && (
                <div data-testid="ecart-batiment-note" className="flex items-start gap-2 text-xs text-amber-700 bg-yellow-500/10 border border-yellow-500/20 rounded-md p-3">
                  <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5" />
                  <span>{t("newEst.wrongBuilding", { m: result.ecart_batiment_m })}</span>
                </div>
              )}
              {result.notes && (
                <div className="flex items-start gap-2 text-xs text-amber-700 bg-yellow-500/10 border border-yellow-500/20 rounded-md p-3">
                  <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5" />
                  <span>{result.notes}</span>
                </div>
              )}
            </div>

            <div className="lg:col-span-5 space-y-4">
              <div className="bg-surface border border-line rounded-xl p-6 shadow-carte space-y-4">
                <h3 className="text-xs font-bold uppercase tracking-[0.2em] text-ink-faint">{t("newEst.editing")}: {active.label}</h3>
                <div>
                  <Label className="text-xs uppercase tracking-widest text-ink-soft">{t("newEst.sectionName")}</Label>
                  <Input data-testid="section-label-input" value={active.label} onChange={(e) => updateActive({ label: e.target.value })} className={`mt-1 ${field}`} />
                </div>
                {/* Bâtiment saisi aux dimensions : pas de contour, pas de
                    satellite. Longueur × largeur, et un prix rond si le couvreur
                    préfère chiffrer de tête plutôt qu'au carré. */}
                {active.saisie === "dimensions" && (
                  <div className="rounded-md border border-line-strong bg-field/50 p-3 space-y-3">
                    <p className="text-[11px] text-ink-faint">{t("newEst.dimsHint")}</p>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <Label className="text-xs uppercase tracking-widest text-ink-soft">{t("newEst.length")}</Label>
                        <Input data-testid="dims-longueur" type="number" min="0" step="0.5" inputMode="decimal"
                          value={active.longueur} onChange={(e) => updateActive({ longueur: e.target.value })}
                          className={`mt-1 ${field}`} />
                      </div>
                      <div>
                        <Label className="text-xs uppercase tracking-widest text-ink-soft">{t("newEst.width")}</Label>
                        <Input data-testid="dims-largeur" type="number" min="0" step="0.5" inputMode="decimal"
                          value={active.largeur} onChange={(e) => updateActive({ largeur: e.target.value })}
                          className={`mt-1 ${field}`} />
                      </div>
                    </div>
                    <p className="text-xs text-ink-soft" data-testid="dims-resultat">
                      {t("newEst.dimsArea", {
                        sol: Math.round(solSection(active)).toLocaleString(),
                        toit: Math.round(aireSection(active)).toLocaleString(),
                      })}
                    </p>
                    <div className="border-t border-line pt-3">
                      <Label className="text-xs uppercase tracking-widest text-ink-soft">{t("newEst.lumpPrice")}</Label>
                      <div className="grid grid-cols-2 gap-3 mt-1">
                        <Input data-testid="dims-forfait" type="number" min="0" step="1" inputMode="decimal"
                          placeholder={t("newEst.lumpPlaceholder")}
                          value={active.forfait} onChange={(e) => updateActive({ forfait: e.target.value })}
                          className={field} />
                        <Input data-testid="dims-cout" type="number" min="0" step="1" inputMode="decimal"
                          placeholder={t("newEst.lumpCost")}
                          value={active.coutForfait} onChange={(e) => updateActive({ coutForfait: e.target.value })}
                          className={field} />
                      </div>
                      <p className="text-[11px] text-ink-faint mt-1.5">
                        {Number(active.forfait) > 0 ? t("newEst.lumpOn") : t("newEst.lumpOff")}
                      </p>
                    </div>
                  </div>
                )}
                {/* Type de toit : l'IA le devinait sans qu'on puisse la corriger.
                    Il devient saisissable — indispensable en mode manuel, utile
                    aussi quand la détection se trompe sur un toit complexe. */}
                <div>
                  <Label className="text-xs uppercase tracking-widest text-ink-soft">{t("estimate.roofType")}</Label>
                  <Select value={active.roofType || "gable"} onValueChange={(v) => updateActive({ roofType: v, roofTypeTouched: true })}>
                    <SelectTrigger data-testid="roof-type-select" className={`mt-1 ${field}`}><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-surface border-line-strong text-ink">
                      {["gable", "hip", "flat", "shed", "complex"].map((rt) => (
                        <SelectItem key={rt} value={rt} className="focus:bg-orange-600 focus:text-white">
                          {t(`roofTypes.${rt}`)}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-widest text-ink-soft">{t("newEst.roofPitch")}</Label>
                  <Select value={pitchDefinie(active.pitch) ? String(active.pitch.value) : undefined}
                          onValueChange={(v) => updateActive({ pitch: PITCH_OPTIONS.find((p) => String(p.value) === v), pitchTouched: true })}>
                    <SelectTrigger data-testid="pitch-select"
                      className={`mt-1 ${field} ${pitchDefinie(active.pitch) ? "" : "border-amber-500"}`}>
                      <SelectValue placeholder={t("newEst.pitchChoose")} />
                    </SelectTrigger>
                    <SelectContent className="bg-surface border-line-strong text-ink">
                      {PITCH_OPTIONS.map((p) => (
                        <SelectItem key={p.label} value={String(p.value)} className="focus:bg-orange-600 focus:text-white">{p.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {/* Pente encore vide : on rappelle ce qui manque, et on montre
                      ce que l'IA a cru voir — comme une piste, pas comme une
                      mesure. C'est le couvreur qui tranche. */}
                  {!pitchDefinie(active.pitch) && (
                    <p data-testid="pitch-manquante" className="text-xs text-amber-700 mt-1.5">
                      {t("newEst.pitchRequired")}
                      {result?.estimated_pitch && !mesureParSolar(active)
                        ? ` ${t("newEst.pitchAiHint")} ${result.estimated_pitch}.`
                        : ""}
                    </p>
                  )}
                  {active.solar?.disponible && (
                    <div className="mt-1.5">
                      <p data-testid="section-solar-badge" className="text-xs text-brand-ink">
                        {t("newEst.solarBadge")} : {active.solar.pente_principale === "flat" ? t("newEst.solarFlat") : active.solar.pente_principale}
                        {" · "}{Number(active.solar.surface_toit_pi2).toLocaleString()} {t("units.sqft")}
                        {/* Date de l'imagerie : sans elle, le couvreur ne peut pas
                            juger si la mesure décrit encore le bâtiment. */}
                        {active.solar.imagerie_annee && (
                          <span className="text-ink-faint"> · {t("newEst.solarImagery")} {active.solar.imagerie_annee}</span>
                        )}
                      </p>
                      <label className="flex items-center gap-2 text-xs text-ink-soft cursor-pointer mt-1.5">
                        <input
                          type="checkbox"
                          data-testid="use-solar-checkbox"
                          checked={!!active.useSolar}
                          onChange={(e) => updateActive({ useSolar: e.target.checked, useSolarTouched: true })}
                          className="accent-orange-600 h-3.5 w-3.5"
                        />
                        {t("newEst.useSolar")}
                      </label>
                      {active.useSolar && (
                        <p className="text-[11px] text-ink-faint mt-1">{t("newEst.useSolarHint")}</p>
                      )}
                      {/* Le bâtiment rendu n'est pas celui qu'on visait. */}
                      {(active.solarEcart >= SOLAR_ECART_DOUTEUX_M || active.solarHorsBoite) && (
                        <p data-testid="solar-ecart" className="text-[11px] text-amber-700 mt-1.5">
                          {active.solarHorsBoite
                            ? t("newEst.solarOffTarget")
                            : t("newEst.solarFarWarning", { m: active.solarEcart })}
                        </p>
                      )}
                      {/* Sortie de secours, toujours offerte : le rectangle bleu
                          vient de Google et ne se déplace pas. Quand ce n'est pas
                          le bon bâtiment, il faut pouvoir le refuser et tracer —
                          sinon la section est bloquée sur une mesure fausse. */}
                      {active.useSolar && (
                        <button
                          onClick={() => {
                            const centre = active.solarPoint
                              ? latLngVersFraction(active.solarPoint.lat, active.solarPoint.lng, result)
                              : [0.5, 0.5];
                            const demi = Math.min(0.25, 3 / (result.ground_width_m || 66));
                            const demiY = Math.min(0.25, 3 / (result.ground_height_m || 66));
                            const c = (v) => Math.min(1, Math.max(0, v));
                            const polygon = [
                              [c(centre[0] - demi), c(centre[1] - demiY)], [c(centre[0] + demi), c(centre[1] - demiY)],
                              [c(centre[0] + demi), c(centre[1] + demiY)], [c(centre[0] - demi), c(centre[1] + demiY)],
                            ];
                            updateActive({
                              useSolar: false, useSolarTouched: true, polygon,
                              footprint: footprintFromPolygon(polygon, result.ground_width_m, result.ground_height_m),
                            });
                          }}
                          data-testid="tracer-a-la-main"
                          className="mt-2 text-[11px] font-semibold text-brand-ink hover:underline text-left"
                        >
                          {t("newEst.traceInstead")}
                        </button>
                      )}
                      {/* Pans du toit. Leur somme redonne exactement le toit
                          complet, donc en décocher un donne un total juste —
                          le couvreur qui ne refait pas l'aile du garage n'avait
                          jusqu'ici que le tout ou rien. */}
                      {active.useSolar && segmentsSolar(active).length > 1 && (
                        <div className="mt-3 border-t border-line pt-2.5">
                          <p className="text-[11px] uppercase tracking-widest text-ink-faint mb-1.5">
                            {t("newEst.segments")}
                          </p>
                          <div className="space-y-1">
                            {segmentsSolar(active).map((seg) => {
                              const retenu = !(active.segExclus || []).includes(seg.id);
                              return (
                                <label key={seg.id} data-testid={`segment-${seg.id}`}
                                  className="flex items-center gap-2 text-xs cursor-pointer">
                                  <input
                                    type="checkbox"
                                    checked={retenu}
                                    onChange={() => basculerSegment(seg.id)}
                                    className="accent-orange-600 h-3.5 w-3.5 shrink-0"
                                  />
                                  <span className={retenu ? "text-ink-soft" : "text-ink-faint line-through"}>
                                    {seg.pente === "flat" ? t("newEst.solarFlat") : seg.pente}
                                    {" · "}
                                    {Number(seg.surface_toit_pi2).toLocaleString()} {t("units.sqft")}
                                  </span>
                                </label>
                              );
                            })}
                          </div>
                          <p data-testid="segments-total" className="text-xs font-semibold text-brand-ink mt-2">
                            {t("newEst.segmentsKept", {
                              n: segmentsRetenus(active).length,
                              total: segmentsSolar(active).length,
                              sqft: Math.round(aireSection(active)).toLocaleString(),
                            })}
                          </p>
                        </div>
                      )}
                    </div>
                  )}
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-widest text-ink-soft">{t("newEst.footprintTune")}</Label>
                  <Input data-testid="footprint-input" type="number" value={Math.round(active.footprint)} onChange={(e) => updateActive({ footprint: Number(e.target.value) })} className={`mt-1 ${field}`} />
                </div>
                {/* Tout ce qui relève du chiffrage n'existe qu'en mode soumission :
                    une mesure seule n'a ni client, ni matériaux, ni PDF. */}
                {!modeMesure && <>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs uppercase tracking-widest text-ink-soft">{t("newEst.clientName")}</Label>
                    <Input data-testid="client-name-input" value={clientName} onChange={(e) => setClientName(e.target.value)} className={`mt-1 ${field}`} />
                  </div>
                  <div>
                    <Label className="text-xs uppercase tracking-widest text-ink-soft">{t("newEst.clientEmail")}</Label>
                    <Input data-testid="client-email-input" type="email" value={clientEmail} onChange={(e) => setClientEmail(e.target.value)} className={`mt-1 ${field}`} />
                  </div>
                </div>
                {/* Vendeur assigné : la liste vit dans Réglages → Modèle de
                    soumission (forfait Team+). Son nom et son numéro direct
                    remplacent le titulaire dans « Préparé par » sur le PDF. */}
                {(settings?.vendeurs || []).filter((v) => (v.nom || "").trim()).length > 0 && (
                  <div>
                    <Label className="text-xs uppercase tracking-widest text-ink-soft">{fr ? "Vendeur assigné" : "Assigned salesperson"}</Label>
                    <select
                      data-testid="vendeur-select"
                      value={vendeurIdx}
                      onChange={(e) => setVendeurIdx(e.target.value)}
                      className="w-full mt-1 bg-field border border-line-strong focus:border-orange-500 rounded-md px-3 py-2 text-sm text-ink outline-none"
                    >
                      <option value="">{fr ? "Aucun" : "None"}</option>
                      {settings.vendeurs.map((v, i) => (
                        (v.nom || "").trim() ? (
                          <option key={i} value={String(i)}>
                            {v.nom}{(v.telephone || "").trim() ? ` — ${v.telephone}` : ""}
                          </option>
                        ) : null
                      ))}
                    </select>
                  </div>
                )}
                <ChoixMateriaux settings={settings} valeur={choix} onChange={setChoix} platsPresents={platsPresents} lang={quoteLang} />
                <div>
                  <Label className="text-xs uppercase tracking-widest text-ink-soft">{t("newEst.quoteLang")}</Label>
                  <Select value={quoteLang} onValueChange={(v) => { quoteLangTouched.current = true; setQuoteLang(v); }}>
                    <SelectTrigger data-testid="estimate-quote-language-select" className={`mt-1 ${field}`}><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-surface border-line-strong text-ink">
                      {paireLangues(settings?.country).map((l) => (
                        <SelectItem key={l} value={l} className="focus:bg-orange-600 focus:text-white">{NOMS_LANGUES[l]}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-widest text-ink-soft">{t("newEst.quoteStyle")}</Label>
                  <Select value={quoteStyle} onValueChange={setQuoteStyle}>
                    <SelectTrigger data-testid="quote-style-select" className={`mt-1 ${field}`}><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-surface border-line-strong text-ink">
                      <SelectItem value="detaillee" className="focus:bg-orange-600 focus:text-white">{t("newEst.styleDetailed")}</SelectItem>
                      <SelectItem value="forfaitaire" className="focus:bg-orange-600 focus:text-white">{t("newEst.styleLump")}</SelectItem>
                    </SelectContent>
                  </Select>
                  {quoteStyle === "forfaitaire" && (
                    <p className="text-xs text-ink-faint mt-1">{t("newEst.lumpHint")}</p>
                  )}
                </div>
                </>}
              </div>

              {/* Toujours visible, dans les deux modes : au moment d'enregistrer,
                  le couvreur ne voit à l'écran que le bâtiment qu'il ajuste. Les
                  autres doivent rester sous les yeux, avec l'addition posée. */}
              <TotalMesure batiments={batimentsRecap} activeId={activeId} onSelect={setActiveId} />

              {!modeMesure && (
                <EstimateBreakdown calc={calc} settings={settings} sections={sectionsBD} masquerListe footprintSqft={totalFootprint} roofType={sections[0].roofType} pitchLabel={sections[0].pitch.label} />
              )}

              {/* Rien ne part tant qu'un bâtiment n'a pas sa pente : la nommer
                  ici évite au couvreur de découvrir le trou en relisant le PDF. */}
              {sansPente.length > 0 && (
                <p data-testid="blocage-pente" className="text-xs text-amber-700 -mb-1">
                  {t("newEst.pitchBlock")} {sansPente.map((sec) => sec.label).join(", ")}
                </p>
              )}
              <Button onClick={modeMesure ? saveMesure : save} disabled={saving || sansPente.length > 0} data-testid="save-estimate-button" className="w-full h-12 bg-orange-600 hover:bg-orange-700 rounded-md font-semibold disabled:opacity-50">
                {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Save className="h-4 w-4 mr-2" /> {modeMesure ? (fr ? "Enregistrer la mesure" : "Save measurement") : t("newEst.saveBtn")}</>}
              </Button>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
}
